import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { filter, map, switchMap, tap } from 'rxjs';

import * as dateUtils from '@shared/utils/date.utils';

import { CellarService } from '@app/services';
import { CellarActions } from '@app/store/actions';
import type { CellarData } from '@app/types/cellar-data.type';

const STORAGE_KEY = 'evcnt_cellar';

@Injectable()
export class CellarEffects {
  readonly #actions$ = inject(Actions);
  readonly #cellarService = inject(CellarService);

  loadPersistant$: any = createEffect((): any =>
    this.#actions$.pipe(
      ofType(CellarActions.loadPersistantCellar),
      map(() => {
        const storageCellar = this.#cellarService.getValue(STORAGE_KEY);
        const cellar: Record<string, { value: CellarData; expires: string | undefined }> =
          storageCellar ? JSON.parse(storageCellar) : {};

        return Object.keys(cellar).reduce((all: Record<string, CellarData>, current: string) => {
          const { value, expires }: { value: CellarData; expires: string | undefined } =
            cellar[current];
          const isValid: boolean = !expires || dateUtils.isBefore(new Date(), new Date(expires));

          return isValid ? { ...all, [current]: value } : all;
        }, {});
      }),
      switchMap((toSave: Record<string, CellarData>) => [
        CellarActions.setData({ data: { ...toSave } }),
      ]),
    ),
  );

  setData$: any = createEffect(
    (): any =>
      this.#actions$.pipe(
        ofType(CellarActions.setData),
        filter(
          ({ persistant }: { data: Record<string, CellarData>; persistant?: boolean }) =>
            !!persistant,
        ),
        tap(({ data }: { data: Record<string, CellarData> }) =>
          Object.keys(data).forEach((key: string) => {
            const value: CellarData = data[key];
            const storedValue: { value: CellarData; expires: string | undefined } = {
              value: data[key],
              expires: value.validity
                ? dateUtils.toISOString(
                    dateUtils.add(
                      dateUtils.startOf(new Date(), value.validity as dateUtils.DateUnit),
                      1,
                      value.validity as dateUtils.DateUnit,
                    ),
                  )
                : undefined,
            };

            this.#cellarService.addValue(STORAGE_KEY, key, storedValue);
          }),
        ),
      ),
    { dispatch: false },
  );
}
